// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { Form, Input, InputNumber } from "antd";
import { useDispatch, useSelector } from "react-redux";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
} from "../../utils/Constants";
import {
  getDepartmentListThunk,
  deleteDepartmentThunk,
  addDepartmentThunk,
  editDepartmentThunk,
} from "../../store/thunks/departmentsThunk";

import media from "../../media";
import EditForm from "../../components/molecules/Forms/editForm";
import { selectedRowInfo } from "../../store/slices/authSlice";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
const Departments = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const departments = useSelector((state) => state.departments.list) || [];
  const departmentsLoading = useSelector((state) => state.departments.loading);
  const storePageInfo = useSelector((state) => state.departments.pageInfo);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [filteredDepartments, setFilteredDepartments] = useState([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);

  const [values, setValues] = useState({
    name: "",
    code: "",
    history: "",
    createdAt: "",
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch departments
  const fetchDepartments = async () => {
    setLoadingCount(true);
    const res = await dispatch(
      getDepartmentListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();
    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchDepartments();
  }, [currentPath, dispatch]);

  useEffect(() => {
    if (Array.isArray(departments) && departments.length > 0) {
      setFilteredDepartments(departments);
    }
  }, [departments]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingDepartment(record); // store the record to edit
    tableForm.setFieldsValue({
      name: record.name,
      code: record.code,
    });
    setIsEditModalOpen(true); // open the modal
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Department Name",
      dataIndex: "name",
      editable: true,
      render: (text) => <>{text}</>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Department Code",
      dataIndex: "code",
      editable: true,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => text && formateDateTime(text),
    },
    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",
            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          inputType: "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Department Name",
      name: "name",
      rules: [
        {
          required: true,
          message: "Please enter department name",
        },
      ],
      placeholder: "Enter the department name",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          name: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Department Code",
      name: "code",
      rules: [""],
      placeholder: "Enter the department code",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          code: e.target.value,
        });
      },
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Departments"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name="Add New Department"
          searchClassName="table-search"
          searchPlaceholder="Search Departments Name & Code"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={departments}
          //onFilteredData={setFilteredDepartments}
          searchKeys={["name", "code"]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            name: values?.name,
            code: values?.code,
            createdAt: new Date().toISOString(),
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
            route: currentPath,
          })}
          list={addDepartmentThunk}
          formFields={formFields}
          successMessage="Department added successfully."
          onSuccess={fetchDepartments}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteDepartmentThunk,
              route: currentPath,
              actionName: "department",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />

        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={
                      storeRole === process.env.REACT_APP_SROLE && rowSelection
                    }
                    storeList={filteredDepartments}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getDepartmentListThunk({
                              route: currentPath,
                              pagination,
                            })
                          ),
                        page,
                        pageSize,
                        storeTotalCount
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit Department"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Department Name",
              name: "name",
              rules: [
                { required: true, message: "Please input department name!" },
              ],
              type: "input",
            },
            { label: "Department Code", name: "code", type: "input" },
          ]}
          submitThunk={editDepartmentThunk}
          payloadBuilder={(values) => ({
            id: editingDepartment.key,
            name: values.name,
            code: values.code,
            historySource: "backend",
            historyAction: "update",
            historyUserID: storeLIUId,
          })}
          successMessage="Department updated successfully."
          currentPath={currentPath}
          refreshListThunk={getDepartmentListThunk}
        />
      </div>
    </div>
  );
};
export default Departments;
